import { UserAvatar } from "@/components/molecules/user-avatar";
import { ExternalBadge } from "@/components/molecules/external-badge";
import { Badge } from "@/components/ui/badge";
import type { ClientMemberWithUser } from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import { MemberAction } from "./columns-action";
import { EditProfileDialog } from "./edit-profile-dialog";
import { ScheduleCell } from "./schedule-cell";

/**
 * The team: everybody the agent may name, book and hand a call to.
 *
 * Deliberately says nothing about logins. Who can sign in to the back-office is
 * the other table on this page, and most of the people here cannot — which is
 * the whole reason the two were split.
 */
export function useMembersColumns() {
  const { t } = useTranslation("members");

  const columns: ColumnDef<ClientMemberWithUser>[] = [
    {
      accessorKey: "membership.display_name",
      header: t("table.member"),
      cell: ({ row }) => {
        const member = row.original;
        const displayName =
          member.membership.display_name || member.user?.full_name || "—";
        const isExternal = !!member.membership.adapter;

        return (
          <div className="flex items-center gap-3">
            <UserAvatar
              avatar={member.user?.preferences?.avatar}
              name={displayName}
              email={member.user?.email}
              size="md"
            />
            <div>
              <div className="font-medium flex items-center gap-2">
                {displayName}
                {isExternal && (
                  <ExternalBadge adapter={member.membership.adapter!} />
                )}
              </div>
              {member.membership.role_text && (
                <div className="text-xs text-muted-foreground">
                  {member.membership.role_text}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: "account",
      header: t("table.account"),
      cell: ({ row }) => {
        const user = row.original.user;

        // "No account" is a normal state, not a gap to fill: a colleague who
        // never opens the back-office is still on the team.
        if (!user) {
          return (
            <span className="text-xs text-muted-foreground">
              {t("status.noAccount")}
            </span>
          );
        }

        return <span className="text-sm">{user.email}</span>;
      },
    },
    {
      accessorKey: "membership.schedule_id",
      header: t("table.schedule"),
      cell: ({ row }) => (
        <ScheduleCell scheduleId={row.original.membership.schedule_id} />
      ),
    },
    {
      accessorKey: "membership.accept_call_transfers",
      header: t("table.callTransfers"),
      cell: ({ row }) => {
        const acceptTransfers = row.original.membership.accept_call_transfers;
        if (!acceptTransfers) return null;
        return <Badge>{t("status.enabled")}</Badge>;
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <EditProfileDialog member={row.original} />
          <MemberAction member={row.original} />
        </div>
      ),
    },
  ];

  return columns;
}
